import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock3,
  LogIn,
  LogOut,
  CalendarDays,
  User2,
  CheckCircle2,
} from "lucide-react";
import { clearPortalSession } from "../../utils/portalAuth";
import { apiRequest } from "../../utils/apiRequest";
import FullPageSpinner from "../../components/FullPageSpinner";

const statusClasses = {
  Closed: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  Open: "bg-amber-100 text-amber-700 border border-amber-200",
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDisplayTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const formatDisplayDateTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const formatClockOutDisplay = (shiftDate, clockOut) => {
  if (!clockOut) return "—";

  const clockOutDate = new Date(clockOut);
  if (Number.isNaN(clockOutDate.getTime())) return "—";

  if (!shiftDate) {
    return formatDisplayDateTime(clockOut);
  }

  const shiftDateObj = new Date(shiftDate);
  if (Number.isNaN(shiftDateObj.getTime())) {
    return formatDisplayDateTime(clockOut);
  }

  const shiftDayKey = `${shiftDateObj.getFullYear()}-${shiftDateObj.getMonth()}-${shiftDateObj.getDate()}`;
  const clockOutDayKey = `${clockOutDate.getFullYear()}-${clockOutDate.getMonth()}-${clockOutDate.getDate()}`;

  return shiftDayKey === clockOutDayKey
    ? formatDisplayTime(clockOut)
    : formatDisplayDateTime(clockOut);
};

const formatHoursWorked = (totalMinutes) => {
  const safeMinutes = Math.max(0, Number(totalMinutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

export default function StaffDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("staff_portal_token");

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [actionFeedback, setActionFeedback] = useState(null);
  const [highlightAction, setHighlightAction] = useState("");

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showResetPinModal, setShowResetPinModal] = useState(false);
  const [pinForm, setPinForm] = useState({
    current_pin: "",
    new_pin: "",
    confirm_pin: "",
  });
  const [pinError, setPinError] = useState("");
  const [pinSuccess, setPinSuccess] = useState("");
  const [pinLoading, setPinLoading] = useState(false);

  const clockInAudioRef = useRef(null);
  const clockOutAudioRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const highlightTimerRef = useRef(null);
  const profileMenuRef = useRef(null);

  const playSound = (audioRef) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = 0;
    audio.play().catch(() => {});
  };

  const triggerVisualFeedback = (type) => {
    const isClockIn = type === "clock-in";

    setActionFeedback({
      type,
      message: isClockIn
        ? "Clocked in successfully"
        : "Clocked out successfully",
    });
    setHighlightAction(type);

    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
    }

    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }

    feedbackTimerRef.current = setTimeout(() => {
      setActionFeedback(null);
    }, 2500);

    highlightTimerRef.current = setTimeout(() => {
      setHighlightAction("");
    }, 1800);
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError("");

    const res = await apiRequest({
      script: "staff-dashboard",
      token,
      data: {},
    });

    if (!res?.success) {
      if (res?.error?.toLowerCase().includes("token")) {
        clearPortalSession();
        navigate("/login", { replace: true });
        return;
      }

      setError(res?.error || "Failed to load dashboard");
      setLoading(false);
      return;
    }

    setDashboard(res);
    setLoading(false);
  }, [token, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!dashboard?.today?.is_open) return;

    const interval = setInterval(() => {
      fetchDashboard();
    }, 30000);

    return () => clearInterval(interval);
  }, [dashboard?.today?.is_open, fetchDashboard]);

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    clearPortalSession();
    navigate("/login", { replace: true });
  };

  const handleClockIn = async () => {
    if (dashboard?.today?.is_open) return;

    setProcessing(true);
    setError("");

    const res = await apiRequest({
      script: "staff-clock-in",
      token,
      data: {},
    });

    setProcessing(false);

    if (!res?.success) {
      setError(res?.error || "Failed to clock in");
      return;
    }

    playSound(clockInAudioRef);
    triggerVisualFeedback("clock-in");
    fetchDashboard();
  };

  const handleClockOut = async () => {
    if (!dashboard?.today?.is_open) return;

    setProcessing(true);
    setError("");

    const res = await apiRequest({
      script: "staff-clock-out",
      token,
      data: {},
    });

    setProcessing(false);

    if (!res?.success) {
      setError(res?.error || "Failed to clock out");
      return;
    }

    playSound(clockOutAudioRef);
    triggerVisualFeedback("clock-out");
    fetchDashboard();
  };

  const handleResetPin = async () => {
    setPinError("");
    setPinSuccess("");

    if (!pinForm.current_pin || !pinForm.new_pin || !pinForm.confirm_pin) {
      setPinError("All fields are required.");
      return;
    }

    if (!/^\d{4}$/.test(pinForm.current_pin)) {
      setPinError("Current PIN must be exactly 4 digits.");
      return;
    }

    if (!/^\d{4}$/.test(pinForm.new_pin)) {
      setPinError("New PIN must be exactly 4 digits.");
      return;
    }

    if (!/^\d{4}$/.test(pinForm.confirm_pin)) {
      setPinError("Confirm PIN must be exactly 4 digits.");
      return;
    }

    if (pinForm.new_pin !== pinForm.confirm_pin) {
      setPinError("New PIN and confirm PIN do not match.");
      return;
    }

    if (pinForm.current_pin === pinForm.new_pin) {
      setPinError("New PIN must be different from current PIN.");
      return;
    }

    setPinLoading(true);

    const res = await apiRequest({
      script: "staff-reset-pin",
      token,
      data: {
        current_pin: pinForm.current_pin,
        new_pin: pinForm.new_pin,
      },
    });

    setPinLoading(false);

    if (!res?.success) {
      setPinError(res?.error || "Failed to reset PIN.");
      return;
    }

    setPinSuccess(res?.message || "PIN updated successfully.");

    setPinForm({
      current_pin: "",
      new_pin: "",
      confirm_pin: "",
    });

    setTimeout(() => {
      setShowResetPinModal(false);
      setPinSuccess("");
    }, 1200);
  };

  const staff = dashboard?.staff || {};
  const today = useMemo(() => {
    return (
      dashboard?.today || {
        clock_in: null,
        clock_out: null,
        hours_worked_minutes: 0,
        is_open: false,
      }
    );
  }, [dashboard]);

  const recentActivity = dashboard?.recent_activity || [];
  const isClockedIn = Boolean(today?.is_open);

  const todaySummary = useMemo(() => {
    return {
      clockIn: formatDisplayDateTime(today.clock_in),
      clockOut: formatDisplayDateTime(today.clock_out),
      hoursWorked: formatHoursWorked(today.hours_worked_minutes),
    };
  }, [today]);

  const thisWeekHours = formatHoursWorked(dashboard?.this_week_minutes);
  const totalSessions = dashboard?.total_sessions || 0;

  const isClockInHighlight = highlightAction === "clock-in";
  const isClockOutHighlight = highlightAction === "clock-out";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Dee Delicious Staff Portal
            </h1>
            <p className="text-sm text-slate-500">
              Welcome back, {staff.first_name || "Staff"}{" "}
              {staff.last_name || ""}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setShowProfileMenu((prev) => !prev)}
                className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 ring-1 ring-orange-200 transition hover:scale-[1.02]"
              >
                <User2 size={20} className="text-orange-700" />
              </button>

              {showProfileMenu ? (
                <div className="absolute right-0 top-14 z-20 w-44 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setShowProfileMenu(false);
                      setPinError("");
                      setPinSuccess("");
                      setPinForm({
                        current_pin: "",
                        new_pin: "",
                        confirm_pin: "",
                      });
                      setShowResetPinModal(true);
                    }}
                    className="block w-full px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    Reset PIN
                  </button>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="block w-full border-t border-slate-100 px-4 py-3 text-left text-sm font-medium text-red-600 transition hover:bg-red-50"
                  >
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
        {actionFeedback ? (
          <div
            className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-all duration-300 ${
              actionFeedback.type === "clock-in"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-amber-200 bg-amber-50 text-amber-700"
            }`}
          >
            <CheckCircle2 size={18} />
            <span className="font-medium">{actionFeedback.message}</span>
          </div>
        ) : null}

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div
            className={`rounded-3xl border bg-white p-6 shadow-sm transition-all duration-500 lg:col-span-2 ${
              isClockInHighlight
                ? "border-emerald-300 ring-4 ring-emerald-100"
                : isClockOutHighlight
                  ? "border-amber-300 ring-4 ring-amber-100"
                  : "border-slate-200"
            }`}
          >
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Current Status
                </p>

                <div className="mt-2 flex items-center gap-3">
                  <h2 className="text-3xl font-bold text-slate-900">
                    {isClockedIn ? "Clocked In" : "Clocked Out"}
                  </h2>

                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-medium transition-all duration-500 ${
                      isClockedIn
                        ? "border border-amber-200 bg-amber-100 text-amber-700"
                        : "border border-slate-200 bg-slate-100 text-slate-700"
                    } ${
                      isClockInHighlight || isClockOutHighlight
                        ? "scale-105"
                        : "scale-100"
                    }`}
                  >
                    {isClockedIn ? "Open" : "Off Shift"}
                  </span>
                </div>

                <p className="mt-3 text-sm text-slate-500">
                  {isClockedIn
                    ? `Shift started at ${formatDisplayDateTime(today.clock_in)}`
                    : today.clock_in && today.clock_out
                      ? `Last shift ended at ${formatDisplayDateTime(today.clock_out)}`
                      : "You have not started a shift yet."}
                </p>

                <p className="mt-2 text-sm text-slate-500">
                  Staff ID:{" "}
                  <span className="font-medium text-slate-700">
                    {staff.staff_id || "—"}
                  </span>
                </p>
              </div>

              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:w-auto">
                <button
                  onClick={handleClockIn}
                  disabled={isClockedIn || processing || loading}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-4 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    isClockInHighlight
                      ? "bg-emerald-600 shadow-lg shadow-emerald-200"
                      : "bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 shadow-sm"
                  }`}
                >
                  <LogIn size={18} />
                  Clock In
                </button>

                <button
                  onClick={handleClockOut}
                  disabled={!isClockedIn || processing || loading}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl border px-5 py-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    isClockOutHighlight
                      ? "border-amber-300 bg-amber-50 text-amber-700 shadow-lg shadow-amber-100"
                      : "border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 hover:from-orange-100 hover:to-amber-100"
                  }`}
                >
                  <LogOut size={18} />
                  Clock Out
                </button>
              </div>
            </div>
          </div>

          <div
            className={`rounded-3xl border bg-white p-6 shadow-sm transition-all duration-500 ${
              isClockInHighlight
                ? "border-emerald-300 ring-4 ring-emerald-100"
                : isClockOutHighlight
                  ? "border-amber-300 ring-4 ring-amber-100"
                  : "border-slate-200"
            }`}
          >
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-blue-100 ring-1 ring-sky-200">
                <Clock3 size={18} className="text-sky-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Current/Recent Shift
                </h3>
                <p className="text-sm text-slate-500">Live shift summary</p>
              </div>
            </div>

            <div className="space-y-4">
              <div
                className={`rounded-2xl p-4 transition-all duration-500 ${
                  isClockInHighlight
                    ? "bg-emerald-50 ring-2 ring-emerald-100"
                    : "bg-sky-50"
                }`}
              >
                <p className="text-sm text-slate-500">Shift Started At</p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {todaySummary.clockIn}
                </p>
              </div>

              <div
                className={`rounded-2xl p-4 transition-all duration-500 ${
                  isClockOutHighlight
                    ? "bg-amber-50 ring-2 ring-amber-100"
                    : "bg-orange-50"
                }`}
              >
                <p className="text-sm text-slate-500">Shift Ended At</p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {todaySummary.clockOut}
                </p>
              </div>

              <div className="rounded-2xl bg-violet-50 p-4">
                <p className="text-sm text-slate-500">Hours Worked</p>
                <p className="mt-1 text-xl font-bold text-violet-900">
                  {todaySummary.hoursWorked}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-6">
          <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">This Week</p>
                <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-blue-900">
                  {thisWeekHours}
                </h3>
              </div>

              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-2xl bg-white/70 ring-1 ring-blue-200">
                <CalendarDays size={20} className="text-blue-700" />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-violet-600">Shifts</p>
                <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-violet-900">
                  {totalSessions}
                </h3>
              </div>

              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-2xl bg-white/70 ring-1 ring-violet-200">
                <Clock3 size={20} className="text-violet-700" />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-orange-50 px-6 py-5">
            <h3 className="text-lg font-semibold text-slate-900">
              Recent Activity
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Your recent clock-in and clock-out history
            </p>
          </div>

          <div className="max-h-[420px] overflow-auto">
            <table className="w-full min-w-[760px]">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Date
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Clock In
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Clock Out
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Hours Worked
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      Loading activity...
                    </td>
                  </tr>
                ) : recentActivity.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No attendance activity yet.
                    </td>
                  </tr>
                ) : (
                  recentActivity.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`last:border-b-0 ${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                      } border-b border-slate-100`}
                    >
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatDisplayDate(item.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatDisplayTime(item.clock_in)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatClockOutDisplay(item.date, item.clock_out)}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                        {formatHoursWorked(item.hours_worked_minutes)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            statusClasses[item.status] ||
                            "border border-slate-200 bg-slate-100 text-slate-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showResetPinModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  Reset PIN
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Enter your current PIN and your new PIN.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowResetPinModal(false);
                  setPinError("");
                  setPinSuccess("");
                }}
                className="rounded-xl px-3 py-2 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Current PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pinForm.current_pin}
                  onChange={(e) =>
                    setPinForm((prev) => ({
                      ...prev,
                      current_pin: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-base outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Enter current PIN"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  New PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pinForm.new_pin}
                  onChange={(e) =>
                    setPinForm((prev) => ({
                      ...prev,
                      new_pin: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-base outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Enter new PIN"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Confirm New PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pinForm.confirm_pin}
                  onChange={(e) =>
                    setPinForm((prev) => ({
                      ...prev,
                      confirm_pin: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-base outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Confirm new PIN"
                />
              </div>

              {pinError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {pinError}
                </div>
              ) : null}

              {pinSuccess ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {pinSuccess}
                </div>
              ) : null}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPinModal(false);
                    setPinError("");
                    setPinSuccess("");
                  }}
                  className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleResetPin}
                  disabled={pinLoading}
                  className="rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-3 text-sm font-medium text-white transition hover:from-orange-600 hover:to-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {pinLoading ? "Updating..." : "Update PIN"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <FullPageSpinner
        show={processing}
        text={isClockedIn ? "Clocking out..." : "Clocking in..."}
      />

      <FullPageSpinner show={pinLoading} />

      <audio ref={clockInAudioRef} preload="auto">
        <source src="/sounds/clock-in.mp3" type="audio/mpeg" />
      </audio>

      <audio ref={clockOutAudioRef} preload="auto">
        <source src="/sounds/clock-out.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
}
